// src/components/ConciergerieGuide.jsx
//
// Owner-facing guide to conciergerie mode, opened from the Alertes page
// (/alerts/guide). All text lives in the locales under
// "conciergerieGuide" - sections of paragraphs + bullet lists, then an
// FAQ - so it is translated like the rest of the owner dashboard.
// Keep it in step with documentations/conciergerie-plan.md.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Button, Paper, Stack, Box,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

const asArray = (v) => (Array.isArray(v) ? v : []);

export default function ConciergerieGuide() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sections = asArray(t('conciergerieGuide.sections', { returnObjects: true }));
  const faq = asArray(t('conciergerieGuide.faq', { returnObjects: true }));

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button variant="text" onClick={() => navigate('/alerts')} sx={{ mb: 1 }}>
        ← {t('conciergerieGuide.back')}
      </Button>
      <Typography variant="h4" sx={{ mb: 1 }}>{t('conciergerieGuide.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{t('conciergerieGuide.intro')}</Typography>

      <Stack spacing={2.5}>
        {sections.map((s, i) => (
          <Paper key={s.title} variant="outlined" sx={{ p: 2.5 }} component="section">
            <Typography variant="h6" sx={{ mb: 1 }}>{i + 1}. {s.title}</Typography>
            {asArray(s.paragraphs).map((p) => (
              <Typography key={p} sx={{ mb: 1 }}>{p}</Typography>
            ))}
            {asArray(s.bullets).length > 0 && (
              <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                {s.bullets.map((b) => <Typography key={b} component="li" sx={{ mb: 0.5 }}>{b}</Typography>)}
              </Box>
            )}
          </Paper>
        ))}
      </Stack>

      {faq.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mt: 4, mb: 1.5 }}>{t('conciergerieGuide.faqTitle')}</Typography>
          {faq.map((item) => (
            <Accordion key={item.q} disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Container>
  );
}
